import { useTranslation } from '@/i18n/useTranslation';

export interface ExitButtonProps {
  readonly onClick: () => void;
}

/**
 * Exit, as the design draws it: a small pill in the page's `Top bar`, OUTSIDE the card, opposite the
 * language selector [Figma · `Exit button`, instanced in the `Top bar` of every flow frame — e.g.
 * `12055:10267` (`02 · Check-in — Desktop`), `12446:27969` (`Overlay · Exit — clear this session?`)].
 *
 * **It is not `BackLink` and it does not replace it.** Back moves one step inside the flow and lives in the
 * CARD's header strip; Exit ends the whole session and lives on the page above the card. The frames draw
 * both on the same screen — `02 · Check-in — Desktop` carries a visible `Exit button` at 360,42 and a
 * `Back button` in the card's `Left` slot — which is why the structural gate paired the two against each
 * other on every screen and reported `dy 185–253`: it was matching the only control the app rendered
 * against the one it did not.
 *
 * Values read from the frame on 2026-09-10, identical at 375 / 820 / 1440: 69 x 29, a full pill, 6px of gap
 * between a 16px icon and the label, 12px of side padding, and the label 14/17 semibold in
 * `--muted-foreground` (#585756, the token the frame's raw hex resolves to). Nothing here scales with the
 * viewport — only the bar it sits in does.
 *
 * ⚠️ **29px tall, which is below the 44px touch floor (convention skill § 9) and below even the 36px the
 * Operator sanctioned for Back on 2026-08-13.** The frame decides a control's size where a frame exists
 * (§ 8), and § 9's recorded exception names Back, text inputs and selects — not this. So it is written down
 * rather than absorbed: the departure is real, and the right end state is design raising the control.
 *
 * The hit area is deliberately NOT padded out to 44 behind a 29px face. That trick is available and it is
 * the wrong trade here for the same reason `BackLink` gives for tolerating a small Back: this control ends
 * the candidate's session, so an invisible 8px halo around it at the top corner of the screen buys
 * accessibility by making an accidental, unrecoverable tap MORE likely. A candidate who misses Exit taps
 * again; a candidate who hits it by accident loses their application. The confirm overlay is the real
 * mitigation — see `ExitConfirmDialog`.
 */
export function ExitButton({ onClick }: ExitButtonProps) {
  const t = useTranslation();

  return (
    <button
      className="kiosk-focusable flex items-center gap-1.5 rounded-[var(--radius-full)] px-3 py-1.5 text-sm font-semibold leading-[17px] text-[var(--muted-foreground)]"
      onClick={onClick}
      type="button"
    >
      {/*
        Lucide's `log-out`, drawn inline rather than loaded as an asset — the same reasoning as `BackLink`'s
        chevron: an asset with a colour baked into it cannot follow the control it belongs to, and
        `currentColor` can.

        The 24-unit viewBox in a 16px box is what reproduces the frame exactly, and it is worth stating
        because the numbers look arbitrary otherwise. Figma renders this instance as three strokes of
        1.333px — which is 2 x 16/24, the 24-grid stroke scaled down — around boxes of 4x12 @ 2,2 · 3.33x6.67
        @ 10.67,4.67 · 8x0 @ 6,8. Those are exactly `log-out`'s door, arrowhead and shaft at 16/24 scale, so
        the browser derives all six numbers from `strokeWidth={2}` on this viewBox instead of anyone
        transcribing them.
      */}
      <svg
        aria-hidden="true"
        className="size-4"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        viewBox="0 0 24 24"
      >
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        <path d="m16 17 5-5-5-5" />
        <path d="M21 12H9" />
      </svg>
      {t('exit.control')}
    </button>
  );
}
